import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Download, InfoOutlined } from '@mui/icons-material';
import { comparisonsApi } from '../../services/benchmarkApi';
import { formatMoney, formatNumber, projectLabel } from './benchmarkFormat';

const money = (v) =>
  v == null || v === '' ? 'N/A' : formatMoney(v);

const num = (v, d = 2) =>
  v == null || v === '' ? 'N/A' : formatNumber(v, d);

const pct = (v) =>
  v == null || v === ''
    ? 'N/A'
    : `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(1)}%`;

/** Soft cell banding for % Dev / Source vs Bench (matches legend chips). */
const devBandSx = (v) => {
  if (v == null || v === '') {
    return {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      px: 0.75,
      bgcolor: 'rgba(0,0,0,0.04)',
      color: 'text.disabled',
    };
  }
  const a = Math.abs(Number(v));
  if (a <= 5) {
    return {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      px: 0.75,
      bgcolor: '#e8f5e9',
      color: '#1b5e20',
      fontWeight: 600,
    };
  }
  if (a <= 10) {
    return {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      px: 0.75,
      bgcolor: '#fff3e0',
      color: '#e65100',
      fontWeight: 600,
    };
  }
  return {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    px: 0.75,
    bgcolor: '#ffebee',
    color: '#b71c1c',
    fontWeight: 600,
  };
};

const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/**
 * Boss Benchmark Comparison Grid — budget code × source + targets.
 * Min/Max, expand lines, Export (no currency filter).
 */
const BenchmarkComparisonGridTab = ({ comparisonId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [structureType, setStructureType] = useState('CBS');
  const [viewMetric, setViewMetric] = useState('BUDGET');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCode, setDetailCode] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    if (!comparisonId) {
      setData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await comparisonsApi.budgetCodeGrid(comparisonId, {
          structureType,
          viewMetric,
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load comparison grid');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [comparisonId, structureType, viewMetric]);

  const openDetail = useCallback(
    async (budgetCode) => {
      if (!comparisonId || !budgetCode) return;
      setDetailCode(budgetCode);
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailError('');
      setDetailData(null);
      try {
        const res = await comparisonsApi.budgetCodeLines(comparisonId, budgetCode);
        setDetailData(res.data);
      } catch (e) {
        setDetailError(e.message || 'Failed to load line details');
      } finally {
        setDetailLoading(false);
      }
    },
    [comparisonId]
  );

  const columns = useMemo(() => {
    if (!data) return [];
    const source = data.sourceProject || {};
    const targets = data.targetProjects || [];
    const cols = [
      {
        field: 'budgetCode',
        headerName: 'Budget Code',
        width: 130,
      },
      {
        field: 'description',
        headerName: 'Description',
        width: 180,
        minWidth: 140,
      },
      {
        field: 'sourceAmount',
        headerName: `Source ${projectLabel(source) || source.projectNumber || ''}`.trim(),
        width: 140,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => money(v),
      },
    ];
    targets.forEach((t, idx) => {
      const pid = t.projectId;
      cols.push({
        field: `t${pid}_amt`,
        headerName: `${projectLabel(t) || t.projectNumber || `T${idx + 1}`}`,
        width: 130,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => money(v),
      });
      cols.push({
        field: `t${pid}_dev`,
        headerName: '% Dev',
        width: 96,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Box sx={devBandSx(params.value)}>{pct(params.value)}</Box>
        ),
      });
    });
    cols.push(
      {
        field: 'avg',
        headerName: 'Avg',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => money(v),
      },
      {
        field: 'median',
        headerName: 'Median',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => money(v),
      },
      {
        field: 'min',
        headerName: 'Min',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => money(v),
      },
      {
        field: 'max',
        headerName: 'Max',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => money(v),
      },
      {
        field: 'stdDev',
        headerName: 'Std Dev',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => money(v),
      },
      {
        field: 'stdDevPct',
        headerName: 'Std Dev %',
        width: 100,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => (v == null ? 'N/A' : `${Number(v).toFixed(2)}%`),
      },
      {
        field: 'sourceVsBenchmarkPct',
        headerName: 'Source vs Bench %',
        width: 140,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Box sx={devBandSx(params.value)}>{pct(params.value)}</Box>
        ),
      },
      {
        field: '_detail',
        headerName: 'Details',
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Tooltip title="Expand: qty / unit rate / budget & actual lines">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openDetail(params.row.budgetCode);
              }}
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      }
    );
    return cols;
  }, [data, openDetail]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    return data.rows.map((r, i) => {
      const row = {
        id: r.budgetCode || i,
        budgetCode: r.budgetCode,
        description: r.description || '',
        sourceAmount: r.sourceAmount,
        avg: r.avg,
        median: r.median,
        min: r.min,
        max: r.max,
        stdDev: r.stdDev,
        stdDevPct: r.stdDevPct,
        sourceVsBenchmarkPct: r.sourceVsBenchmarkPct,
      };
      (r.targets || []).forEach((t) => {
        row[`t${t.projectId}_amt`] = t.amount;
        row[`t${t.projectId}_dev`] = t.pctDev;
      });
      return row;
    });
  }, [data]);

  const handleExport = () => {
    if (!data?.rows?.length) return;
    const targets = data.targetProjects || [];
    const headers = [
      'Budget Code',
      'Description',
      `Source ${projectLabel(data.sourceProject) || data.sourceProject?.projectNumber || ''}`.trim(),
      ...targets.flatMap((t) => [
        `${projectLabel(t) || t.projectNumber || t.projectId}`,
        `${projectLabel(t) || t.projectNumber || t.projectId} % Dev`,
      ]),
      'Avg',
      'Median',
      'Min',
      'Max',
      'Std Dev',
      'Std Dev %',
      'Source vs Bench %',
    ];
    const lines = [headers.map(csvEscape).join(',')];
    data.rows.forEach((r) => {
      const cells = [
        r.budgetCode,
        r.description,
        r.sourceAmount,
        ...targets.flatMap((t) => {
          const cell = (r.targets || []).find((x) => String(x.projectId) === String(t.projectId));
          return [cell?.amount ?? '', cell?.pctDev ?? ''];
        }),
        r.avg,
        r.median,
        r.min,
        r.max,
        r.stdDev,
        r.stdDevPct,
        r.sourceVsBenchmarkPct,
      ];
      lines.push(cells.map(csvEscape).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comparison-grid-${comparisonId}-${structureType}-${viewMetric}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const lineColumns = useMemo(
    () => [
      { field: 'projectLabel', headerName: 'Project', width: 140 },
      { field: 'transactionType', headerName: 'Type', width: 110 },
      { field: 'boqItemNumber', headerName: 'BoQ / Item', width: 120 },
      { field: 'description', headerName: 'Description', flex: 1, minWidth: 160 },
      { field: 'quantity', headerName: 'Qty', width: 100, type: 'number', align: 'right', headerAlign: 'right', valueFormatter: (v) => num(v) },
      { field: 'unit', headerName: 'Unit', width: 70 },
      { field: 'unitPrice', headerName: 'Unit rate', width: 120, type: 'number', align: 'right', headerAlign: 'right', valueFormatter: (v) => num(v) },
      { field: 'amount', headerName: 'Amount', width: 130, type: 'number', align: 'right', headerAlign: 'right', valueFormatter: (v) => money(v) },
    ],
    []
  );

  const lineRows = useMemo(() => {
    if (!detailData?.projects) return [];
    const out = [];
    detailData.projects.forEach((p) => {
      const label = `${p.source ? 'Source · ' : ''}${projectLabel(p) || p.projectNumber || p.projectId}`;
      (p.lines || []).forEach((line, i) => {
        out.push({
          id: `${p.projectId}-${line.costTransactionId || line.id || i}`,
          projectLabel: label,
          transactionType: line.transactionType,
          boqItemNumber: line.boqItemNumber,
          description: line.description,
          quantity: line.quantity,
          unit: line.unit,
          unitPrice: line.unitPrice,
          amount: line.amount,
        });
      });
    });
    return out;
  }, [detailData]);

  if (!comparisonId) {
    return (
      <Alert severity="info">
        Create or load a comparison first, then open the Comparison tab.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.25,
          mb: 1.5,
          minHeight: 40,
        }}
      >
        <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>
          Compare by budget code
        </Typography>

        <Select
          size="small"
          value={structureType}
          onChange={(e) => setStructureType(e.target.value)}
          displayEmpty
          inputProps={{ 'aria-label': 'Cost structure' }}
          sx={{
            height: 36,
            minWidth: 128,
            bgcolor: 'background.paper',
            '& .MuiSelect-select': {
              py: 0,
              display: 'flex',
              alignItems: 'center',
              height: 36,
              boxSizing: 'border-box',
            },
          }}
        >
          <MenuItem value="CBS">CBS</MenuItem>
          <MenuItem value="MASTERFORMAT">MasterFormat</MenuItem>
          <MenuItem value="UNIFORMAT">UniFormat</MenuItem>
          <MenuItem value="WBS">WBS</MenuItem>
        </Select>

        <Select
          size="small"
          value={viewMetric}
          onChange={(e) => setViewMetric(e.target.value)}
          displayEmpty
          inputProps={{ 'aria-label': 'View' }}
          sx={{
            height: 36,
            minWidth: 168,
            bgcolor: 'background.paper',
            '& .MuiSelect-select': {
              py: 0,
              display: 'flex',
              alignItems: 'center',
              height: 36,
              boxSizing: 'border-box',
            },
          }}
        >
          <MenuItem value="BUDGET">Budget (revised)</MenuItem>
          <MenuItem value="ORIGINAL">Estimate / BoQ</MenuItem>
          <MenuItem value="ACTUAL">Actual cost</MenuItem>
        </Select>

        {data?.sourceProject && (
          <Box
            sx={{
              height: 36,
              px: 1.5,
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: '8px',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: '0.8125rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              lineHeight: 1,
              boxSizing: 'border-box',
            }}
          >
            Source: {projectLabel(data.sourceProject)}
          </Box>
        )}

        <Button
          variant="outlined"
          size="small"
          startIcon={<Download sx={{ fontSize: '18px !important' }} />}
          onClick={handleExport}
          disabled={!data?.rows?.length}
          sx={{
            textTransform: 'none',
            height: 36,
            minHeight: '36px !important',
            py: 0,
            px: 1.75,
            borderRadius: '8px',
            boxSizing: 'border-box',
            lineHeight: 1,
            '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
          }}
        >
          Export
        </Button>

        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            ml: { xs: 0, sm: 'auto' },
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Chip size="small" sx={{ height: 28, bgcolor: '#e8f5e9', color: '#2e7d32' }} label="≤±5%" />
          <Chip size="small" sx={{ height: 28, bgcolor: '#fff3e0', color: '#ed6c02' }} label="±5–10%" />
          <Chip size="small" sx={{ height: 28, bgcolor: '#ffebee', color: '#d32f2f' }} label=">±10%" />
          <Chip size="small" variant="outlined" sx={{ height: 28 }} label="N/A = missing" />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {!loading && data && (
        <Box sx={{ height: 440, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            pageSizeOptions={[15, 30, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
            onRowDoubleClick={(params) => openDetail(params.row.budgetCode)}
            disableRowSelectionOnClick
            disableVirtualization
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'rgba(139, 21, 56, 0.06)',
                borderBottom: '1px solid rgba(139, 21, 56, 0.18)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#8B1538',
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                bgcolor: 'rgba(0, 0, 0, 0.015)',
              },
              /* Sticky Budget Code + Description (community DataGrid CSS pin) */
              '& .MuiDataGrid-cell[data-field="budgetCode"]': {
                position: 'sticky',
                left: 0,
                zIndex: 4,
                bgcolor: '#fff',
                borderRight: '1px solid rgba(0,0,0,0.08)',
                fontWeight: 600,
              },
              '& .MuiDataGrid-columnHeader[data-field="budgetCode"]': {
                position: 'sticky',
                left: 0,
                zIndex: 5,
                bgcolor: 'rgba(139, 21, 56, 0.08)',
                borderRight: '1px solid rgba(0,0,0,0.08)',
              },
              '& .MuiDataGrid-cell[data-field="description"]': {
                position: 'sticky',
                left: 130,
                zIndex: 4,
                bgcolor: '#fff',
                borderRight: '1px solid rgba(0,0,0,0.12)',
                boxShadow: '2px 0 4px rgba(0,0,0,0.04)',
              },
              '& .MuiDataGrid-columnHeader[data-field="description"]': {
                position: 'sticky',
                left: 130,
                zIndex: 5,
                bgcolor: 'rgba(139, 21, 56, 0.08)',
                borderRight: '1px solid rgba(0,0,0,0.12)',
                boxShadow: '2px 0 4px rgba(0,0,0,0.04)',
              },
              '& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field="budgetCode"], & .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field="description"]':
                {
                  bgcolor: '#fafafa',
                },
              '& .MuiDataGrid-cell[data-field$="_dev"], & .MuiDataGrid-cell[data-field="sourceVsBenchmarkPct"]':
                {
                  p: 0,
                },
            }}
          />
        </Box>
      )}

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px', minHeight: 480 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Budget code lines
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
            {detailCode} — qty, unit rate, budget &amp; actual (source + targets)
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {!detailLoading && !detailError && lineRows.length === 0 && (
            <Alert severity="info">
              No transaction lines found for this code across selected projects.
            </Alert>
          )}
          {!detailLoading && lineRows.length > 0 && (
            <Box sx={{ height: 400 }}>
              <DataGrid
                rows={lineRows}
                columns={lineColumns}
                density="compact"
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                disableRowSelectionOnClick
                sx={{ border: 'none' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            variant="contained"
            onClick={() => setDetailOpen(false)}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BenchmarkComparisonGridTab;
